import moment from 'jalali-moment';
import type { WorkhourSchedule } from '@/modules/profile/utils/workhourSchedules';
import { getFeedbackCreatedAtValue, parseFeedbackCreatedAt } from '@/common/utils/formatRelativeFeedbackTime';

/**
 * منطق خالصِ «پیشنهادهای سریع» هوم پزشک — بدون React و بدون شبکه، تا با تست قفل شود.
 * هر پیشنهاد باید دلیلش را با عدد و تاریخ واقعیِ همان پزشک بگوید، نه متن عمومی.
 */

const WEEKDAY_EN = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export const weekdayEnglish = (isoDate: string) => WEEKDAY_EN[moment(isoDate, 'YYYY-MM-DD').day()];

const toMinutes = (hour: string) => {
  const [h, m] = hour.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** بازه‌ی تاریخ‌دارِ ساعت کاری (from_date/to_date) فقط در همان بازه معتبر است. */
const isScheduleActiveOn = (schedule: WorkhourSchedule, isoDate: string) => {
  if (schedule.from_date && moment(schedule.from_date).format('YYYY-MM-DD') > isoDate) return false;
  if (schedule.to_date && moment(schedule.to_date).format('YYYY-MM-DD') < isoDate) return false;
  return true;
};

export const schedulesOnDate = (schedules: WorkhourSchedule[], isoDate: string) => {
  const day = weekdayEnglish(isoDate);
  return schedules
    .filter(item => item.day === day && isScheduleActiveOn(item, isoDate))
    .sort((a, b) => toMinutes(a.from_hour) - toMinutes(b.from_hour));
};

export const isMinuteInSchedules = (schedules: WorkhourSchedule[], minuteOfDay: number) =>
  schedules.some(item => {
    const from = toMinutes(item.from_hour);
    const to = toMinutes(item.to_hour);
    return minuteOfDay >= from && minuteOfDay < to;
  });

export const formatScheduleRanges = (schedules: WorkhourSchedule[]) =>
  schedules
    .map(item => `${item.from_hour.slice(0, 5)} تا ${item.to_hour.slice(0, 5)}`)
    .filter((value, index, all) => all.indexOf(value) === index)
    .join('، ');

/** پیشنهاد «روزِ انتخابی در ساعت کاری نیست» — فقط وقتی هیچ مرکزِ مورد نظر آن روز ساعت کاری ندارد. */
export const isDayOutsideWorkhours = (schedulesByCenter: Record<string, WorkhourSchedule[]>, isoDate: string) => {
  const centers = Object.values(schedulesByCenter);
  // پزشکی که اصلاً ساعت کاری ثبت‌شده ندارد اینجا پیشنهاد نمی‌گیرد (مسیر رشد همین را می‌گوید)
  if (!centers.some(list => list.length > 0)) return false;
  return centers.every(list => schedulesOnDate(list, isoDate).length === 0);
};

export const PERSIAN_DIGITS = (value: string | number) => String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/** منطقه‌ی زمانی دستگاه؛ ایران دو نام دارد. */
const IRAN_TIMEZONES = new Set(['Asia/Tehran', 'Iran']);

export const detectAbroadTimezone = (timeZone?: string | null, offsetMinutes?: number): string | null => {
  if (timeZone) return IRAN_TIMEZONES.has(timeZone) ? null : timeZone;
  // بدون نام منطقه، فقط اختلاف ساعت: ایران +۳:۳۰ (getTimezoneOffset = -210)
  if (offsetMinutes == null || offsetMinutes === -210) return null;
  return `UTC${offsetMinutes <= 0 ? '+' : '-'}${Math.floor(Math.abs(offsetMinutes) / 60)}:${String(Math.abs(offsetMinutes) % 60).padStart(2, '0')}`;
};

/** نام شهرِ منطقه‌ی زمانی برای متن («Europe/Berlin» → «Berlin»). */
export const timezoneCity = (timeZone: string) => timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;

export const LONG_ABSENCE_DAYS = 21;

export const daysSince = (previousIso: string | null | undefined, now = new Date()) => {
  if (!previousIso) return null;
  const previous = new Date(previousIso);
  if (Number.isNaN(previous.getTime())) return null;
  return Math.floor((now.getTime() - previous.getTime()) / 86_400_000);
};

/**
 * دسته‌های گلایه‌ای که با ساعت کاری/مرخصی درست می‌شوند. فقط عبارت‌های مشخص — «نبود» تنها
 * («مشکلی نبود») عمداً نیست تا پزشک بی‌دلیل پیشنهاد نگیرد.
 */
export const COMPLAINT_CATEGORIES = [
  {
    key: 'absent',
    label: 'در مطب نبودید',
    patterns: [
      /(در|تو|توی)\s*مطب\s*(ن?بودن|نبود|حضور\s*نداشت|تشریف\s*نداشت)/,
      /دکتر\s*(نبود|نیومد|نیامد|نیامدند|نیومدن|حضور\s*نداشت|تشریف\s*نداشت)/,
      /پزشک\s*(نبود|نیومد|نیامد|حضور\s*نداشت|تشریف\s*نداشت)/,
      /مطب\s*(تعطیل|بسته)\s*(بود|بودن)/,
    ],
  },
  {
    key: 'waiting',
    label: 'معطل شدند',
    patterns: [/معطل(?!\s*ن)/, /(\d+|[۰-۹]+|یک|دو|سه|چند)\s*ساعت\s*(منتظر|انتظار|معطل)/, /انتظار\s*(طولانی|زیاد)/, /دیر\s*(اومد|آمد|اومدن|آمدند)/, /ت[اأ]خیر\s*(زیاد|داشت|طولانی)/],
  },
] as const;

export type ComplaintCategoryKey = (typeof COMPLAINT_CATEGORIES)[number]['key'];

export interface ComplaintSummary {
  total: number;
  days: number;
  byCategory: Partial<Record<ComplaintCategoryKey, number>>;
  topCategory: ComplaintCategoryKey | null;
  sample?: string;
  /** چند روز پیش آخرین گلایه ثبت شد */
  daysSinceLast: number | null;
  /** چند نظرِ بی‌گلایه بعد از آخرین گلایه آمده — نشانه‌ی اصلاح */
  cleanSinceLast: number;
  /** هنوز ارزش گفتن دارد: آخرین گلایه تازه است و هنوز نشانه‌ی اصلاح نیامده */
  active: boolean;
}

/** آخرین گلایه باید در این بازه باشد تا پیشنهاد بیاید. */
export const COMPLAINT_RECENT_DAYS = 30;
/** این تعداد نظرِ بی‌گلایه پس از آخرین گلایه = پزشک اصلاح کرده؛ پیشنهاد نمی‌آید. */
export const CLEAN_REVIEWS_TO_RESOLVE = 3;

const reviewText = (item: Record<string, unknown>) =>
  String(item.description ?? item.comment ?? item.text ?? '').replace(/\s+/g, ' ').trim();

export const classifyComplaint = (text: string): ComplaintCategoryKey | null => {
  for (const category of COMPLAINT_CATEGORIES) {
    if (category.patterns.some(pattern => pattern.test(text))) return category.key;
  }
  return null;
};

/**
 * فیلدهای ساختاریافته‌ی فرم نظر راوی بر متن مقدم‌اند:
 * - visit_status=not_visited با دلیل «بسته بودن مطب» / «عدم حضو(ر) پزشک»
 * - waiting_time: ۰ کمتر از نیم ساعت، ۱ کمتر از ۱ ساعت، ۲ بیشتر از ۱ ساعت، ۳ بیشتر
 */
export const LONG_WAIT_CODE = 2;

const structuredComplaint = (item: Record<string, unknown>): ComplaintCategoryKey | null => {
  const reasons = String(item.not_visited_reasons ?? '');
  if (item.visit_status === 'not_visited' && /بسته\s*بودن\s*مطب|عدم\s*حضور?\s*پزشک/.test(reasons)) return 'absent';
  const wait = item.waiting_time;
  if (wait != null && wait !== '' && Number(wait) >= LONG_WAIT_CODE) return 'waiting';
  return null;
};

export const MIN_WAITING_REPORTS = 2;

export const summarizeComplaintReviews = (
  items: Array<Record<string, unknown>>,
  options: { days?: number; now?: Date } = {},
): ComplaintSummary => {
  const days = options.days ?? 60;
  const since = (options.now ?? new Date()).getTime() - days * 86_400_000;
  const byCategory: Partial<Record<ComplaintCategoryKey, number>> = {};
  let total = 0;
  let sample: string | undefined;
  const timeline: { at: number; complaint: ComplaintCategoryKey | null }[] = [];

  for (const item of items) {
    const createdMs = parseFeedbackCreatedAt(getFeedbackCreatedAtValue(item))?.getTime() ?? NaN;
    if (!Number.isFinite(createdMs) || createdMs < since) continue;
    const text = reviewText(item);
    const fromText = text ? classifyComplaint(text) : null;
    const category = structuredComplaint(item) ?? fromText;
    timeline.push({ at: createdMs, complaint: category });
    if (!category) continue;
    total += 1;
    byCategory[category] = (byCategory[category] ?? 0) + 1;
    // نمونه فقط از متنی که خودش گلایه است — نظرِ مثبتی که فقط فیلد «انتظار» دارد شاهد نیست
    if (!sample && fromText) sample = text.length > 90 ? `${text.slice(0, 90)}…` : text;
  }

  // یک گزارش معطلی تنها کافی نیست (نمونه‌ی تک، نویز است)؛ یک گزارش «نبودن پزشک» کافی است
  if ((byCategory.waiting ?? 0) < MIN_WAITING_REPORTS) {
    total -= byCategory.waiting ?? 0;
    delete byCategory.waiting;
  }
  if (!total) sample = undefined;

  const topCategory =
    (Object.entries(byCategory).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))[0]?.[0] as ComplaintCategoryKey | undefined) ?? null;

  // روند: آخرین گلایه‌ی شمرده‌شده کی بود و بعدش چند نظرِ سالم آمد
  const counted = new Set(Object.keys(byCategory));
  const ordered = timeline.sort((a, b) => a.at - b.at);
  const lastIndex = ordered.map(entry => !!entry.complaint && counted.has(entry.complaint)).lastIndexOf(true);
  const nowMs = (options.now ?? new Date()).getTime();
  const daysSinceLast = lastIndex >= 0 ? Math.floor((nowMs - ordered[lastIndex].at) / 86_400_000) : null;
  const cleanSinceLast = lastIndex >= 0 ? ordered.slice(lastIndex + 1).filter(entry => !entry.complaint).length : 0;
  const active =
    total > 0 && daysSinceLast !== null && daysSinceLast <= COMPLAINT_RECENT_DAYS && cleanSinceLast < CLEAN_REVIEWS_TO_RESOLVE;

  return { total, days, byCategory, topCategory, sample, daysSinceLast, cleanSinceLast, active };
};

export const complaintCategoryLabel = (key: ComplaintCategoryKey) =>
  COMPLAINT_CATEGORIES.find(category => category.key === key)?.label ?? '';

/** کمتر از این، روز عملاً «بی‌ساعت کاری» است (مثلاً یک بازه‌ی ۵ دقیقه‌ای جامانده). */
export const SHORT_DAY_MINUTES = 30;

export interface MinuteRange {
  from: number;
  to: number;
}

/** بازه‌های روز (دقیقه از نیمه‌شب)، ادغام‌شده و مرتب؛ هم‌پوشان‌ها و چسبیده‌ها یکی می‌شوند. */
export const mergedDayRanges = (schedules: WorkhourSchedule[]): MinuteRange[] => {
  const ranges = schedules
    .map(item => ({ from: toMinutes(item.from_hour), to: toMinutes(item.to_hour) }))
    .filter(range => range.to > range.from)
    .sort((a, b) => a.from - b.from);
  const merged: MinuteRange[] = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range.from <= last.to) last.to = Math.max(last.to, range.to);
    else merged.push({ ...range });
  }
  return merged;
};

export const totalMinutes = (ranges: MinuteRange[]) => ranges.reduce((sum, range) => sum + (range.to - range.from), 0);

export const formatMinute = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

export const formatRanges = (ranges: MinuteRange[]) => ranges.map(r => `${formatMinute(r.from)} تا ${formatMinute(r.to)}`).join('، ');

/**
 * وضعیت روز در ساعت کاری مراکزِ مورد نظر: off (هیچ)، short (کمتر از ۳۰ دقیقه — احتمالاً جامانده)،
 * normal. پزشکی که هیچ ساعتی در هیچ روزی ندارد null می‌گیرد (داده‌ای برای قضاوت نیست).
 */
export const dayWorkhourState = (
  schedulesByCenter: Record<string, WorkhourSchedule[]>,
  isoDate: string,
): { state: 'off' | 'short' | 'normal'; ranges: MinuteRange[]; minutes: number } | null => {
  const all = Object.values(schedulesByCenter);
  if (!all.some(list => list.length > 0)) return null;
  const ranges = mergedDayRanges(all.flatMap(list => schedulesOnDate(list, isoDate)));
  const minutes = totalMinutes(ranges);
  return { state: minutes === 0 ? 'off' : minutes < SHORT_DAY_MINUTES ? 'short' : 'normal', ranges, minutes };
};

export type VacationPreset = { id: string; label: string; hint?: string; fromMinute: number; toMinute: number };

/**
 * گزینه‌های آماده‌ی «اعلام تعطیلی» برای یک روز، از ساعت کاری واقعی همان روز:
 * - امروز: «از الان تا آخر روز» (فقط اگر هنوز ساعت کاری‌ای مانده یا ساعتی تعریف نشده)
 * - هر شیفت باقی‌مانده («شیفت ۱۸:۰۰ تا ۲۱:۰۰»)؛ شیفت در جریان از «الان» شروع می‌شود
 * - «تمام روز»
 */
export const buildVacationPresets = (ranges: MinuteRange[], options: { nowMinute?: number | null }): VacationPreset[] => {
  const now = options.nowMinute ?? null;
  const presets: VacationPreset[] = [];
  const remaining = ranges
    .map(range => ({ from: now !== null ? Math.max(range.from, now) : range.from, to: range.to }))
    .filter(range => range.to > range.from);

  if (now !== null) {
    presets.push({
      id: 'rest',
      label: 'از الان تا آخر روز',
      hint: remaining.length ? `ساعت کاری باقی‌مانده: ${formatRanges(remaining)}` : undefined,
      fromMinute: now,
      toMinute: 23 * 60 + 59,
    });
  }
  // با یک شیفت، «از الان» و «تمام روز» همان را می‌گویند؛ شیفت جدا فقط وقتی چندتاست معنا دارد
  if (ranges.length > 1) {
    for (const range of remaining) {
      presets.push({
        id: `shift-${range.from}`,
        label: `${now !== null && range.from === now ? 'بقیه‌ی شیفت' : 'شیفت'} ${formatMinute(range.from)} تا ${formatMinute(range.to)}`,
        fromMinute: range.from,
        toMinute: range.to,
      });
    }
  }
  presets.push({ id: 'full', label: 'تمام روز', fromMinute: 0, toMinute: 23 * 60 + 59 });
  return presets;
};

/**
 * میانگین انتظارِ سنجه بلندمدت است و دیر پایین می‌آید؛ اگر چند نظرِ اخیرِ مطب همه «کمتر از نیم ساعت»
 * باشند، پزشک اصلاح کرده و پیشنهاد معطلی نباید بیاید.
 */
export const RECENT_WAITING_SAMPLE = 5;

export const recentWaitingImproved = (items: Array<Record<string, unknown>>): boolean => {
  const recent = items
    .filter(item => item.waiting_time != null && item.waiting_time !== '' && Number.isFinite(Number(item.waiting_time)))
    .map(item => ({ at: parseFeedbackCreatedAt(getFeedbackCreatedAtValue(item))?.getTime() ?? 0, code: Number(item.waiting_time) }))
    .sort((a, b) => b.at - a.at)
    .slice(0, RECENT_WAITING_SAMPLE);
  return recent.length === RECENT_WAITING_SAMPLE && recent.every(entry => entry.code === 0);
};

/**
 * ترتیب کارت‌ها: هرچه زودتر به بیمار آسیب بزند بالاتر (عدد کمتر).
 * ۱) بیمارانی که واقعاً پشت در مطب مانده‌اند (گزارش ویزیت ناموفق)
 * ۲) پزشکِ احتمالاً خارج از کشور بدون مرخصی — بیمارانِ بعدی هم پشت در می‌مانند
 * ۳) گلایه‌ی تازه‌ی بیماران از نبودن/معطلی
 * ۴) تقویمِ ناهماهنگ با حضور
 * ۵) همین الان / امروز و فردا خارج از ساعت کاری
 * ۶) معطلیِ میانگین
 * ۷) روزِ کوتاهِ جامانده، روزِ دورتر بی‌ساعت
 * ۸) «مدتی است سر نزده‌اید» — یادآوریِ نرم
 */
export const suggestionPriority = (dismissKey: string, options: { daysAhead: number }): number => {
  const [kind, detail = ''] = dismissKey.split(':');
  if (kind === 'sanje') {
    if (detail.startsWith('inperson_absence')) return 10;
    if (detail.startsWith('booking_calendar')) return 40;
    return 60;
  }
  if (kind === 'abroad') return 20;
  if (kind === 'complaints') return 30;
  if (kind === 'now-outside') return 50;
  if (kind === 'day-off') return options.daysAhead <= 1 ? 55 : 80;
  if (kind === 'short-day') return 70;
  if (kind === 'long-absence') return 90;
  return 100;
};
